import React, { useState, useEffect } from 'react';
import { format, addDays, subDays, startOfWeek, isSameDay } from 'date-fns';
import { useI18n } from '../i18n/context';
import { getDateFnsLocale } from '../lib/localeRuntime';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar as CalendarComponent } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

const HorizontalDatePicker = ({ selectedDate, onDateChange, className = '' }) => {
  const { t, locale } = useI18n();
  const dfLocale = getDateFnsLocale(locale);
  const [weekDates, setWeekDates] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Инициализируем currentWeek на основе selectedDate, если он передан
  const [currentWeek, setCurrentWeek] = useState(() => {
    return selectedDate && !isNaN(selectedDate.getTime()) ? selectedDate : new Date();
  });

  // Синхронизируем currentWeek с selectedDate при изменении
  useEffect(() => {
    if (selectedDate && !isNaN(selectedDate.getTime())) {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      setCurrentWeek(prev => {
        const prevWeekStart = startOfWeek(prev, { weekStartsOn: 1 });
        // Обновляем currentWeek только если selectedDate находится в другой неделе
        if (weekStart.getTime() !== prevWeekStart.getTime()) {
          return selectedDate;
        }
        return prev;
      });
    }
  }, [selectedDate]);

  // Генерируем даты для текущей недели (Пн-Вскр, 7 дней)
  useEffect(() => {
    try {
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Понедельник
      
      // Генерируем ровно 7 дней (Пн-Вскр)
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStart, i);
        if (!isNaN(date.getTime())) {
          dates.push(new Date(date));
        }
      }
      
      // Убеждаемся, что у нас ровно 7 дней
      if (dates.length === 7) {
        setWeekDates(dates);
      } else {
        console.warn('Expected 7 days, got:', dates.length);
        // Fallback: создаем даты для текущей недели
        const today = new Date();
        const fallbackWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const fallbackDates = [];
        for (let i = 0; i < 7; i++) {
          const date = addDays(fallbackWeekStart, i);
          if (!isNaN(date.getTime())) {
            fallbackDates.push(new Date(date));
          }
        }
        setWeekDates(fallbackDates);
      }
    } catch (error) {
      console.error('Error generating week dates:', error);
      // Fallback: создаем даты для текущей недели
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStart, i);
        if (!isNaN(date.getTime())) {
          dates.push(new Date(date));
        }
      }
      setWeekDates(dates);
    }
  }, [currentWeek]);

  const navigateWeek = (direction) => {
    setCurrentWeek(prev => {
      try {
        const newWeek = direction === 'prev' 
          ? subDays(prev, 7) 
          : addDays(prev, 7);
        
        // Проверяем валидность новой даты
        if (!isNaN(newWeek.getTime())) {
          return newWeek;
        } else {
          console.warn('Invalid date generated, using current date');
          return new Date();
        }
      } catch (error) {
        console.error('Error navigating week:', error);
        return new Date();
      }
    });
  };

  const handleDateClick = (date) => {
    if (date && !isNaN(date.getTime())) {
      onDateChange(date);
    }
  };

  const isToday = (date) => {
    if (!date || isNaN(date.getTime())) return false;
    return isSameDay(date, new Date());
  };

  const isSelected = (date) => {
    if (!date || isNaN(date.getTime()) || !selectedDate || isNaN(selectedDate.getTime())) return false;
    return isSameDay(date, selectedDate);
  };

  const isPastDate = (date) => {
    if (!date || isNaN(date.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const selectedIsToday =
    !!selectedDate && !isNaN(selectedDate.getTime()) && isSameDay(selectedDate, new Date());

  const weekContainsToday =
    weekDates.length === 7 &&
    weekDates.some((d) => d && !isNaN(d.getTime()) && isSameDay(d, new Date()));

  /** Скрываем, если открыта календарная «текущая» неделя и выбран сегодняшний день */
  const showTodayNav = !(weekContainsToday && selectedIsToday);

  const handleGoToToday = () => {
    const today = new Date();
    onDateChange(today);
    setCurrentWeek(today);
  };

  return (
    <div className={`bg-background border rounded-lg ${className}`} style={{ padding: 'var(--space-lg)', overflow: 'hidden' }}>
      {/* Навигация по неделям: стрелки по краям, по центру — диапазон + компактный Today */}
      <div
        className="flex min-w-0 flex-shrink-0 items-center gap-1 sm:gap-2"
        style={{ marginBottom: 'var(--space-lg)' }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateWeek('prev')}
          className="h-8 w-8 shrink-0 p-0 hover:bg-muted touch-manipulation"
          style={{ minWidth: '32px', minHeight: '32px' }}
          type="button"
          aria-label={t('date.prevWeek')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5 sm:gap-2">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="h-8 min-w-0 max-w-[calc(100%-3.5rem)] shrink font-medium text-sm hover:bg-muted sm:max-w-none"
                title={t('date.pickDate')}
              >
                <span className="flex items-center justify-center gap-1.5 truncate sm:gap-2">
                  {weekDates.length === 7 &&
                  weekDates[0] &&
                  !isNaN(weekDates[0].getTime()) &&
                  weekDates[6] &&
                  !isNaN(weekDates[6].getTime())
                    ? `${format(weekDates[0], 'd MMM', { locale: dfLocale })} – ${format(weekDates[6], 'd MMM yyyy', { locale: dfLocale })}`
                    : t('date.pickWeek')}
                  <Calendar className="h-4 w-4 shrink-0" />
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    onDateChange(date);
                    setCurrentWeek(date);
                    setCalendarOpen(false);
                  }
                }}
                locale={dfLocale}
                defaultMonth={selectedDate || new Date()}
                className="rounded-md border"
              />
            </PopoverContent>
          </Popover>

          {showTodayNav ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 px-1.5 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary sm:px-2 sm:text-sm"
              onClick={handleGoToToday}
              title={t('date.goToTodayHint')}
            >
              {t('date.goToToday')}
            </Button>
          ) : null}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateWeek('next')}
          className="h-8 w-8 shrink-0 p-0 hover:bg-muted touch-manipulation"
          style={{ minWidth: '32px', minHeight: '32px' }}
          type="button"
          aria-label={t('date.nextWeek')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Горизонтальная прокрутка дат - только блок с днями прокручивается */}
      <div
        className="horizontal-date-picker-container flex min-w-0 w-full overflow-x-auto scrollbar-hide"
        style={{
          gap: 'var(--space-xs)',
          paddingTop: 'var(--space-md)',
          paddingBottom: 'var(--space-md)',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
        }}
      >
        {weekDates.length === 7 ? weekDates.map((date, index) => {
          // Проверяем валидность даты перед форматированием
          if (!date || isNaN(date.getTime())) {
            return null;
          }
          
          const dayName = format(date, 'EEEEEE', { locale: dfLocale }); // Short weekday
          const dayNumber = format(date, 'd');
          const isCurrentDay = isToday(date);
          const isSelectedDate = isSelected(date);
          const isPast = isPastDate(date);

          return (
            <button
              key={`${date.getTime()}-${index}`}
              onClick={() => handleDateClick(date)}
              className={`
                flex flex-col items-center justify-center rounded-full date-button touch-manipulation
                ${isSelectedDate 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : isCurrentDay 
                    ? 'bg-primary/10 text-primary border-2 border-primary' 
                    : isPast
                      ? 'bg-muted/30 text-muted-foreground border border-muted'
                      : 'bg-background text-foreground border border-border hover:bg-muted active:bg-muted'
                }
              `}
              style={{ 
                // Размеры управляются через CSS медиа-запросы для адаптивности
                minWidth: '48px',
                width: '48px',
                height: '64px',
                flexShrink: 0,
                marginTop: 'var(--space-xs)', 
                marginBottom: 'var(--space-xs)',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <span className="text-xs font-medium whitespace-nowrap" style={{ marginBottom: 'var(--space-xs)' }}>{dayName}</span>
              <span className="text-lg font-semibold">{dayNumber}</span>
            </button>
          );
        }) : (
          // Fallback: показываем сообщение, если дней не 7
          <div className="text-sm text-muted-foreground p-4">
            {t('date.loadingDays', { n: weekDates.length })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalDatePicker;
